import {Component, computed, input} from '@angular/core';
import {RouterLink} from '@angular/router';
import {DatePipe, formatDate} from '@angular/common';
import {MatAnchor} from '@angular/material/button';
import {Platform} from '../../classes/platform';
import {Checkpoint} from '../../classes/checkpoint';
import {CheckpointResult} from '../../classes/checkpoint-result';
import {HasFeaturePipe} from '../../pipes/has-feature.pipe';
import {UrlPrefixStrippingPipe} from '../../pipes/url-prefix-stripping.pipe';
import {DaysFromNowPipe} from '../../pipes/days-from-now.pipe';
import {CodaltComponent} from '../../codalt.component';
import {InfoBannerComponent, relativeDay, StatusBadgeComponent, Tone} from '../../shared/ui';

/** "Vandaag 14:31" / "Gisteren 09:00" / "di 13 okt 14:31". */
export function dayTime(value: Date | string | null | undefined): string {
    if (!value) {
        return '';
    }
    const day = relativeDay(value);
    const prefix = ['Vandaag', 'Gisteren', 'Morgen'].includes(day) ? day : formatDate(value, 'EEE d MMM', 'nl');
    return `${prefix} ${formatDate(value, 'HH:mm', 'nl')}`;
}

const lowerFirst = (text: string) => text ? text[0].toLowerCase() + text.slice(1) : text;

/** Short duration for the config strip: "250 ms", "30 s", "2 min 30 s", "1 uur". */
export function shortMs(value: number | null | undefined): string {
    const ms = Number(value ?? 0);
    if (ms < 1000) {
        return `${ms} ms`;
    }
    const hours = Math.floor(ms / 3_600_000);
    const minutes = Math.floor((ms % 3_600_000) / 60_000);
    const seconds = Math.round((ms % 60_000) / 1000);
    return [hours && `${hours} uur`, minutes && `${minutes} min`, seconds && `${seconds} s`].filter(Boolean).join(' ');
}

/** Status of one check result: tone and label for the status dot and the strip. */
export function resultStatus(result: CheckpointResult): {tone: Tone, label: string} {
    if (!result.finish_date) {
        return {tone: 'neutral', label: 'Bezig'};
    }
    if (result.error) {
        return {tone: 'error', label: 'Error'};
    }
    if (result.warning) {
        return {tone: 'warn', label: 'Waarschuwing'};
    }
    return {tone: 'ok', label: 'Goed'};
}

/** The stored problem text in Dutch where it is a known message; curl errors are shown as they are. */
export function resultProblem(result: CheckpointResult, checkpoint: Checkpoint): string {
    switch (result?.result) {
        case null:
        case undefined:
        case '':
            return '';
        case 'Invalid response code':
            return `Statuscode ${result.response_status_code}, verwacht ${checkpoint?.valid_response_code ?? '-'}`;
        case 'Warning response code':
            return `Statuscode ${result.response_status_code} (waarschuwingscode)`;
        case 'Checksum mismatch':
            return 'Checksum komt niet overeen';
        case 'Minimal content size threshold breached':
            return 'Respons kleiner dan de ondergrens';
        case 'Maximum content size threshold breached':
            return 'Respons groter dan de bovengrens';
        case 'Minimal responseTime threshold breached':
            return 'Reactietijd onder de ondergrens';
        case 'Maximum responseTime threshold breached':
            return 'Reactietijd boven de bovengrens';
        default:
            return result.result;
    }
}

/**
 * Head card of a checkpoint: postponed/failure banners, title with status pill, URL, actions and the strip
 * with the six configuration cells. Projected content: `[actions]` (next to Bewerken) and `[resume]`
 * (button in the postponed banner).
 */
@Component({
    selector: 'app-checkpoint-header',
    templateUrl: './checkpoint-header.component.html',
    styleUrls: ['./checkpoint-header.component.scss'],
    imports: [
        RouterLink,
        DatePipe,
        HasFeaturePipe,
        MatAnchor,
        UrlPrefixStrippingPipe,
        DaysFromNowPipe,
        InfoBannerComponent,
        StatusBadgeComponent,
    ]
})
export class CheckpointHeaderComponent extends CodaltComponent {
    platform = input.required<Platform>();
    checkpoint = input.required<Checkpoint>();
    /** Latest results, newest first; used for the cause and start of a failure. */
    results = input<CheckpointResult[] | null>(null);
    /** Name of who postponed the checkpoint, when known. */
    postponedBy = input<string | null>(null);

    readonly isPostponed = computed(() => {
        const postponedTill = this.checkpoint()?.postponed_till;
        return !!postponedTill && new Date(postponedTill) > new Date();
    });

    readonly status = computed<{tone: Tone, label: string} | null>(() => {
        const checkpoint = this.checkpoint();
        if (!checkpoint?.id) {
            return null;
        }
        if (this.isPostponed()) {
            return {tone: 'warn', label: 'Opgeschort'};
        }
        if (checkpoint.error) {
            return {tone: 'error', label: 'Storing'};
        }
        if (checkpoint.warning) {
            return {tone: 'warn', label: 'Waarschuwing'};
        }
        return {tone: 'ok', label: 'Actief'};
    });

    readonly postponedTill = computed(() => dayTime(this.checkpoint()?.postponed_till));

    /** The failed checks in a row, newest first, counted from the latest finished result. */
    readonly failure = computed(() => {
        const checkpoint = this.checkpoint();
        if (!checkpoint?.error) {
            return null;
        }
        const finished = (this.results() ?? []).filter(result => result.finish_date);
        const streak: CheckpointResult[] = [];
        for (const result of finished) {
            if (!result.error) {
                break;
            }
            streak.push(result);
        }
        const latest = streak[0] ?? checkpoint.checkpoint_result_last_instance;
        const first = streak[streak.length - 1];
        const since = first?.finish_date ?? first?.start_date;
        return {
            since: !since ? null : relativeDay(since) === 'Vandaag' ? formatDate(since, 'HH:mm', 'nl') : dayTime(since).toLowerCase(),
            cause: latest ? lowerFirst(resultProblem(latest, checkpoint)) : '',
            count: streak.length,
        };
    });

    readonly config = computed(() => {
        const checkpoint = this.checkpoint();
        const sslChecked = !!Number(checkpoint?.validate_ssl);
        const interval = Number(checkpoint?.interval ?? 0);
        return {
            level: checkpoint?.level ?? '—',
            responseCode: checkpoint?.valid_response_code ?? '—',
            responseHelp: checkpoint?.warning_response_code ? `Waarschuwing bij ${checkpoint.warning_response_code}` : 'Geen waarschuwingscode',
            retries: `${checkpoint?.retries ?? 0}× · ${shortMs(checkpoint?.retry_delay)} ertussen`,
            interval: interval === 60_000 ? 'Elke minuut' : interval === 3_600_000 ? 'Elk uur' : `Elke ${shortMs(interval)}`,
            sslChecked,
            sslTone: this.sslTone(checkpoint?.ssl_valid_till),
            postponedTill: this.isPostponed() ? this.postponedTill() : '—',
            postponedHelp: this.isPostponed() ? (this.postponedBy() ? `Door ${this.postponedBy()}` : 'Opgeschort') : 'Niet opgeschort',
        };
    });

    /** Same limits as the old color coding: red within a week, orange within two weeks. */
    private sslTone(validTill: Date | string | null | undefined): string {
        if (!validTill) {
            return '';
        }
        const date = new Date(validTill);
        const inDays = (days: number) => {
            const limit = new Date();
            limit.setDate(limit.getDate() + days);
            return limit;
        };
        if (date <= inDays(7)) {
            return 'ui-error';
        }
        return date <= inDays(14) ? 'ui-warn' : '';
    }
}
